import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useState } from 'react';

interface TourStep {
  title: string;
  description: string;
  variant: 'welcome' | 'discover' | 'personalize' | 'ready';
}

const TourIcon = ({ variant }: { variant: TourStep['variant'] }) => {
  return (
    <div className="relative w-32 h-32 sm:w-40 sm:h-40">
      {variant === 'welcome' && (
        <>
          <motion.div
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-28 border-4 border-[#E94E1B] rounded-t-full origin-bottom"
          />
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 translate-x-6 w-3 h-3 rounded-full bg-[#E94E1B]"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.3 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-24 bg-[#E94E1B]/20 rounded-t-full"
          />
        </>
      )}

      {variant === 'discover' && (
        <>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-20 border-4 border-[#E94E1B] rounded-full"
          />
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            className="absolute bottom-4 right-4 w-12 h-4 bg-[#E94E1B] rotate-45 rounded-full origin-left"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.7 }}
            className="absolute top-8 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#E94E1B] opacity-60"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.8 }}
            className="absolute top-12 left-1/2 -translate-x-1/2 translate-x-3 w-3 h-3 rounded-full bg-[#2E2A23] opacity-50"
          />
        </>
      )}

      {variant === 'personalize' && (
        <>
          <div className="absolute top-6 left-1/2 -translate-x-1/2 w-24 h-2 bg-[#E94E1B]/20 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-2 bg-[#E94E1B]/20 rounded-full" />
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-24 h-2 bg-[#E94E1B]/20 rounded-full" />

          <motion.div
            initial={{ x: -20 }}
            animate={{ x: 8 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
            className="absolute top-5 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#E94E1B] rounded-full"
          />
          <motion.div
            initial={{ x: 20 }}
            animate={{ x: -4 }}
            transition={{ duration: 0.8, delay: 0.5, ease: 'easeOut' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[#E94E1B] rounded-full"
          />
          <motion.div
            initial={{ x: -10 }}
            animate={{ x: 6 }}
            transition={{ duration: 0.8, delay: 0.7, ease: 'easeOut' }}
            className="absolute bottom-5 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#2E2A23] rounded-full"
          />
        </>
      )}

      {variant === 'ready' && (
        <>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: 'spring' }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-4 border-[#E94E1B] rounded-full"
          />
          <motion.svg
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#E94E1B"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <motion.path
              d="M5 12l5 5L20 7"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            />
          </motion.svg>
        </>
      )}
    </div>
  );
};

const ProgressDots = ({
  total,
  current,
  onDotClick,
}: {
  total: number;
  current: number;
  onDotClick: (index: number) => void;
}) => {
  return (
    <div className="flex items-center gap-3">
      {Array.from({ length: total }).map((_, index) => (
        <button
          type="button"
          key={index}
          onClick={() => onDotClick(index)}
          className={`transition-all duration-300 rounded-full cursor-pointer ${
            index === current
              ? 'w-8 h-3 bg-[#E94E1B]'
              : index < current
                ? 'w-3 h-3 bg-[#E94E1B]/60 hover:bg-[#E94E1B]/80'
                : 'w-3 h-3 bg-[#E94E1B]/20 hover:bg-[#E94E1B]/40'
          }`}
          aria-label={`Ir para passo ${index + 1}`}
        />
      ))}
    </div>
  );
};

interface WelcomeTourProps {
  onComplete: () => void;
}

export function WelcomeTour({ onComplete }: WelcomeTourProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const steps: TourStep[] = [
    {
      title: 'Bem-vindo à Newsletter',
      description:
        'Sua nova forma de descobrir e acompanhar o melhor conteúdo sobre tecnologia, curado especialmente para você.',
      variant: 'welcome',
    },
    {
      title: 'Descubra conteúdos',
      description:
        'Navegue por uma seleção cuidadosa de artigos, tutoriais e notícias escolhidos por especialistas que entendem o que importa.',
      variant: 'discover',
    },
    {
      title: 'Personalize sua experiência',
      description:
        'Quanto mais você lê, mais inteligente ficamos. A Newsletter aprende suas preferências para entregar exatamente o que você procura.',
      variant: 'personalize',
    },
    {
      title: 'Tudo pronto!',
      description:
        'Você está preparado para começar sua jornada. Explore, salve e leia onde e quando quiser.',
      variant: 'ready',
    },
  ];

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  }, [currentStep, onComplete, steps.length]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const goToStep = (index: number) => {
    setCurrentStep(index);
  };

  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === 'ArrowRight') {
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft' && !isFirstStep) {
        e.preventDefault();
        prevStep();
      }
    },
    [isFirstStep, nextStep, prevStep],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="min-h-screen w-full bg-[#F4F2F2] text-[#2E2A23] font-sans overflow-hidden flex flex-col">
      <div className="w-full px-8 py-6 flex justify-end max-w-4xl mx-auto">
        {!isLastStep && (
          <button
            type="button"
            onClick={() => setCurrentStep(steps.length - 1)}
            className="text-[#888185] text-sm font-medium hover:text-[#E94E1B] transition-colors cursor-pointer"
          >
            Pular
          </button>
        )}
      </div>

      <main className="flex-1 w-full max-w-4xl mx-auto px-8 flex flex-col items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
            className="text-center space-y-8 sm:space-y-12"
          >
            <div className="flex justify-center">
              <TourIcon variant={steps[currentStep].variant} />
            </div>

            <div className="space-y-4 sm:space-y-6 max-w-lg mx-auto">
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight text-[#2E2A23]">
                {steps[currentStep].title}
              </h1>
              <p className="text-[#888185] text-base sm:text-lg leading-relaxed">
                {steps[currentStep].description}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </main>

      <div className="w-full px-8 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="w-24">
            {!isFirstStep && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={prevStep}
                className="text-[#888185] font-medium hover:text-[#E94E1B] transition-colors flex items-center gap-2 cursor-pointer"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Voltar
              </motion.button>
            )}
          </div>

          <ProgressDots total={steps.length} current={currentStep} onDotClick={goToStep} />

          <div className="w-24 flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextStep}
              className={`font-medium transition-all flex items-center gap-2 cursor-pointer ${
                isLastStep
                  ? 'bg-[#E94E1B] text-[#F4F2F2] px-6 py-3 rounded-full hover:bg-[#C43D0F] shadow-lg'
                  : 'text-[#E94E1B] hover:text-[#C43D0F]'
              }`}
            >
              {isLastStep ? (
                'Começar'
              ) : (
                <>
                  Próximo
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </>
              )}
            </motion.button>
          </div>
        </div>
      </div>

      <div className="fixed top-[-20%] left-[-10%] w-[50vw] h-[50vh] bg-[#E94E1B]/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[40vw] h-[40vh] bg-[#2E2A23]/5 rounded-full blur-[100px] pointer-events-none" />
    </div>
  );
}
